/* eslint-disable no-await-in-loop */
import EndCallWarningModal from '@brands/Dashboard/Dashboard/Components/ConfirmCloseModal/ConfirmCloseModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { setShowVideoThumbnail } from '@brands/store/slices/showVideoThumbnailSlice';
import { setIsVisible } from '@brands/store/slices/videoCallCredentialsSlice';
import CallEndIcon from '@mui/icons-material/CallEnd';
import React, { FC, useEffect } from 'react';
import Modal from 'react-modal';

import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import useVideoContext from '../../../hooks/video-hooks/useVideoContext';
import { UserRoleName } from '../../../services/identity/types/UserProfile';
import styles from './styles.module.scss';

const TEN_MINUTES_MS = 10 * 60 * 1000;

type EndCallButtonProps = {
  isWaitingRoom?: boolean;
  endCurrentCall?: boolean;
  appointmentStartTime?: Date | null;
  layout?: 'mini' | 'full';
  onLeaveCall?: () => void;
};

const EndCallButton: FC<EndCallButtonProps> = ({
  isWaitingRoom,
  endCurrentCall,
  appointmentStartTime,
  layout,
  onLeaveCall,
}) => {
  const { room, localTracks, setBackgroundSettings } = useVideoContext();
  const { userInfo } = useAppSelector(selectAuth);
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const [showEndWarning, setShowEndWarning] = React.useState(false);

  const dispatchSuccessEvent = (event: string, video_room_id: string): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      userId: userInfo.id,
      video_room_id,
    });
  };

  const stopCameraAndTracks = async (): Promise<void> => {
    try {
      // Reset background settings
      setBackgroundSettings({ type: 'none', index: 0 });

      // Unpublish and stop all local tracks
      localTracks.forEach((track) => {
        if (track.kind === 'video') {
          room?.localParticipant.unpublishTrack(track);
          track.stop();
        }
      });

      // Release media devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      for (const videoDevice of videoDevices) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: videoDevice.deviceId },
        });
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (error) {
      console.warn('Error stopping video devices:', error);
    }
  };

  const endCall = async (): Promise<void> => {
    dispatch(setIsVisible(false));
    dispatch(setShowVideoThumbnail(false));

    dispatchSuccessEvent('video-room-finished', room?.sid || '');

    // Stop camera and tracks
    await stopCameraAndTracks();
    onLeaveCall?.();

    // Disconnect from the room and navigate if necessary
    if (!isWaitingRoom) {
      room?.disconnect();
      if (userInfo.role.name === UserRoleName.Patient) roleNavigate('/dashboard');
    } else if (userInfo.role.name === UserRoleName.Patient) {
      roleNavigate('/dashboard');
    }
  };

  const shouldShowEndCallWarning = (): boolean => {
    if (!appointmentStartTime) return false;

    const elapsedMs = Date.now() - new Date(appointmentStartTime).getTime();
    return elapsedMs >= 0 && elapsedMs < TEN_MINUTES_MS;
  };

  useEffect(() => {
    (async () => {
      if (endCurrentCall) {
        await endCall();
      }
    })();
  }, [endCurrentCall]);

  return (
    <>
      <button
        type="button"
        className="fs-unmask"
        onClick={() => {
          if (shouldShowEndCallWarning()) {
            setShowEndWarning(true);
          } else {
            endCall();
          }
        }}
        aria-label="End Call"
      >
        <span
          className={`${styles.vcVideoIcon} ${styles.endCallButton}`}
          style={{ width: layout === 'mini' ? '70px' : '80px', height: layout === 'mini' ? '30px' : '40px' }}
        >
          <CallEndIcon />
        </span>
      </button>
      <EndCallWarningModal isOpen={showEndWarning} toggleModal={setShowEndWarning} endCallNow={endCall} />
    </>
  );
};

EndCallButton.defaultProps = {
  isWaitingRoom: false,
  endCurrentCall: false,
  onLeaveCall: undefined,
};

export default EndCallButton;
